/* De documentweergave (ADR 0021): één Versie van een document, met de feedbackpunten erop.
 *
 * Opent over de projectpagina via `?document=<file_id>`, zodat een melding, een taak of een
 * link rechtstreeks naar een Versie kan. Een PDF staat links met de punten erop: klik voor een
 * punt, sleep voor een kader. Rechts de punten met hun draad en status. Open punten van een
 * eerdere versie staan gestippeld op dezelfde plek, zodat ze bij de volgende versie zichtbaar
 * blijven. Een Office-bestand toont de pagina niet; daar krijgt een punt een pagina- of
 * dianummer. De PDF komt via gv-crm-mcp (`/app/file_content`), met de gv-files-app. */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  AlertTriangle, Ban, BellRing, Check, CornerDownRight, ExternalLink, FileText, Loader2, MessageSquarePlus,
  MousePointerClick, Smartphone, Trash2, Undo2, X, ZoomIn, ZoomOut,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { compareRank, type DocumentGroup } from '../../../../supabase/functions/_shared/fileVersions';
import type { Bestand, Bestanden } from '../data/bestanden';
import {
  ankerUitSleep, nummers, openVoorMij, plekLabel, puntenVan, pushLabel, soortVan, statusLabel, telling, verzoekenVan, versieVan,
  type Anker, type FeedbackOverzicht, type FeedbackPunt, type FeedbackVerzoek, type Soort, type VersieRef,
} from '../data/feedback';
import { useBestandsinhoud, useFeedbackActies } from '../hooks';
import { datum, extensie, tijd } from './opmaak';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type Tab = 'open' | 'dicht' | 'alles';

const KLEUR: Record<FeedbackPunt['status'], string> = {
  open: 'border-amber-600 bg-amber-500 text-white',
  resolved: 'border-emerald-700 bg-emerald-600 text-white',
  dismissed: 'border-slate-500 bg-slate-400 text-white',
};
const KADER: Record<FeedbackPunt['status'], string> = {
  open: 'border-amber-500 bg-amber-400/10',
  resolved: 'border-emerald-600 bg-emerald-500/10',
  dismissed: 'border-slate-400 bg-slate-400/10',
};

const NAAM_SOORT: Record<Soort, string> = { pdf: 'Pagina', dia: 'Dia', pagina: 'Pagina', blad: 'Blad', overig: 'Pagina' };

function refVan(f: { file: Bestand }, version: string): VersieRef {
  return { file_id: f.file.id, file_name: f.file.name, folder: f.file.folder, version };
}

export function DocumentWeergave({
  projectId, fileId, bestanden, bestandenLaden, feedback, mijnId, onKiesVersie, onSluit, onVraagFeedback,
}: {
  projectId: string;
  fileId: string;
  bestanden: Bestanden | undefined;
  bestandenLaden: boolean;
  feedback: FeedbackOverzicht | undefined;
  mijnId: string | null;
  onKiesVersie: (fileId: string) => void;
  onSluit: () => void;
  onVraagFeedback: (doc: DocumentGroup<Bestand>, fileId: string) => void;
}) {
  const doc = useMemo(
    () => bestanden?.documenten.find((d) => d.versions.some((v) => v.files.some((f) => f.file.id === fileId))) ?? null,
    [bestanden, fileId],
  );
  const versie = doc ? versieVan(doc, fileId) : null;
  const variant = versie?.files.find((f) => f.file.id === fileId) ?? null;
  const soort: Soort = variant ? soortVan(variant.file.name) : 'overig';
  const [concept, setConcept] = useState<Anker | null>(null);
  const [gekozen, setGekozen] = useState<string | null>(null);

  /* Esc sluit eerst een half geplaatst punt, dan de weergave; de pagina eronder scrolt niet mee. */
  useEffect(() => {
    const vorig = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const toets = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.querySelector('[role="dialog"]')) return;
      if (concept) setConcept(null);
      else onSluit();
    };
    window.addEventListener('keydown', toets);
    return () => {
      document.body.style.overflow = vorig;
      window.removeEventListener('keydown', toets);
    };
  }, [concept, onSluit]);

  useEffect(() => {
    setConcept(null);
  }, [fileId]);

  const inhoud = (
    <div className="fixed inset-0 z-50 flex flex-col bg-background" role="region" aria-label="Documentweergave">
      {!doc || !versie || !variant ? (
        <>
          <Kop titel={bestandenLaden ? 'Document laden…' : 'Document niet gevonden'} onSluit={onSluit} />
          <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-muted-foreground">
            {bestandenLaden ? (
              <Loader2 className="h-6 w-6 animate-spin" aria-label="Laden" />
            ) : (
              <p className="max-w-md">Deze Versie staat niet (meer) in de projectmap. Misschien is hij verplaatst of hernoemd; open de projectmap in SharePoint.</p>
            )}
          </div>
        </>
      ) : (
        <Weergave
          projectId={projectId}
          doc={doc}
          fileId={fileId}
          soort={soort}
          feedback={feedback}
          mijnId={mijnId}
          concept={concept}
          setConcept={setConcept}
          gekozen={gekozen}
          setGekozen={setGekozen}
          onKiesVersie={onKiesVersie}
          onSluit={onSluit}
          onVraagFeedback={onVraagFeedback}
        />
      )}
    </div>
  );
  return createPortal(inhoud, document.body);
}

function Kop({ titel, onSluit, children }: { titel: React.ReactNode; onSluit: () => void; children?: React.ReactNode }) {
  return (
    <header className="flex min-h-14 flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-2">
      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0" onClick={onSluit} aria-label="Sluiten (Esc)">
        <X className="h-5 w-5" />
      </Button>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">{typeof titel === 'string' ? <h1 className="truncate text-base font-semibold">{titel}</h1> : titel}</div>
      {children}
    </header>
  );
}

function Weergave({
  projectId, doc, fileId, soort, feedback, mijnId, concept, setConcept, gekozen, setGekozen, onKiesVersie, onSluit, onVraagFeedback,
}: {
  projectId: string;
  doc: DocumentGroup<Bestand>;
  fileId: string;
  soort: Soort;
  feedback: FeedbackOverzicht | undefined;
  mijnId: string | null;
  concept: Anker | null;
  setConcept: (a: Anker | null) => void;
  gekozen: string | null;
  setGekozen: (id: string | null) => void;
  onKiesVersie: (fileId: string) => void;
  onSluit: () => void;
  onVraagFeedback: (doc: DocumentGroup<Bestand>, fileId: string) => void;
}) {
  const { toast } = useToast();
  const acties = useFeedbackActies(projectId);
  const versie = versieVan(doc, fileId)!;
  const variant = versie.files.find((f) => f.file.id === fileId)!;
  const nieuwste = doc.versions[0]!;
  const isNieuwste = versie === nieuwste;
  const beschikbaar = feedback?.beschikbaar !== false;

  const alle = useMemo(() => (feedback ? puntenVan(doc, feedback.punten) : []), [doc, feedback]);
  const nrs = useMemo(() => nummers(alle), [alle]);
  const verzoeken = useMemo(() => (feedback ? verzoekenVan(doc, feedback.verzoeken) : []), [doc, feedback]);
  const voorMij = openVoorMij(verzoeken, mijnId);
  const rangVan = (id: string) => versieVan(doc, id)?.rank ?? [0];
  /* Op deze versie geplaatst, en open punten van eerdere versies (gestippeld). */
  const hier = alle.filter((p) => p.file_id === fileId);
  const geesten = alle.filter((p) => p.status === 'open' && p.file_id !== fileId && compareRank(rangVan(p.file_id), versie.rank) < 0);

  const pdf = useBestandsinhoud(projectId, soort === 'pdf' ? variant.file : null);
  const [plaatsen, setPlaatsen] = useState(true);
  const [zoom, setZoom] = useState(1);

  const plaats = (tekst: string, anker: Anker) => {
    acties.plaats.mutate(
      { versie: refVan(variant, versie.version), tekst, anker, verzoekId: voorMij[0]?.id ?? null },
      {
        onSuccess: (p) => {
          setConcept(null);
          setGekozen(p.id);
        },
        onError: (e) => toast({ title: 'Punt niet geplaatst', description: (e as Error).message, variant: 'destructive' }),
      },
    );
  };

  return (
    <>
      <Kop
        onSluit={onSluit}
        titel={
          <>
            <FileText className={cn('h-5 w-5 shrink-0', soort === 'pdf' ? 'text-red-700' : 'text-slate-600')} aria-hidden />
            <h1 className="min-w-0 truncate text-base font-semibold" title={variant.file.name}>{doc.title}</h1>
            <div role="group" aria-label="Versie" className="flex flex-wrap items-center gap-1">
              {doc.versions.map((v) => {
                const f = v.files.find((x) => x.parsed.lang === variant.parsed.lang) ?? v.files[0]!;
                const t = telling(alle.filter((p) => v.files.some((x) => x.file.id === p.file_id)));
                const aan = v === versie;
                return (
                  <button
                    key={v.version}
                    type="button"
                    aria-pressed={aan}
                    onClick={() => onKiesVersie(f.file.id)}
                    title={v.updateOf ? `v${v.version}, update van v${v.updateOf}` : undefined}
                    className={cn(
                      'flex items-center gap-1 rounded-full border',
                      /* Een kleine update (3.1.1) is kleiner dan de 3.1 waar hij bij hoort. */
                      v.updateOf ? 'h-6 px-2 text-[11px] font-medium' : 'h-7 px-2.5 text-xs font-semibold',
                      aan ? 'border-foreground bg-foreground text-background' : 'bg-background text-muted-foreground hover:text-foreground',
                    )}
                  >
                    v{v.version}
                    {t.open > 0 && <span className={cn('rounded-full px-1.5 text-[10px]', aan ? 'bg-amber-400 text-black' : 'bg-amber-100 text-amber-900')}>{t.open}</span>}
                  </button>
                );
              })}
            </div>
            {versie.concept && <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-bold tracking-wide text-amber-800">CONCEPT</span>}
            {versie.files.length > 1 && versie.files.map((f) => (
              <button
                key={f.file.id}
                type="button"
                aria-pressed={f.file.id === fileId}
                onClick={() => onKiesVersie(f.file.id)}
                className={cn('rounded border px-1.5 py-px text-[11px] font-semibold', f.file.id === fileId ? 'bg-muted' : 'hover:bg-muted')}
              >
                {f.parsed.lang ?? extensie(f.file.name).toUpperCase()}
              </button>
            ))}
          </>
        }
      >
        <div className="flex items-center gap-1.5">
          {variant.file.web_url && (
            <Button variant="outline" size="sm" asChild>
              <a href={variant.file.web_url} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" />SharePoint</a>
            </Button>
          )}
          {beschikbaar && (
            <Button size="sm" onClick={() => onVraagFeedback(doc, fileId)}>
              <BellRing className="mr-1.5 h-4 w-4" />Vraag feedback
            </Button>
          )}
        </div>
      </Kop>

      {!isNieuwste && (
        <p className="m-0 flex flex-wrap items-center gap-2 border-b bg-sky-50 px-4 py-2 text-sm text-sky-900">
          Je bekijkt v{versie.version}; de nieuwste is v{nieuwste.version}.
          <button type="button" className="font-medium underline underline-offset-2" onClick={() => onKiesVersie(nieuwste.files[0]!.file.id)}>
            Naar v{nieuwste.version}
          </button>
        </p>
      )}
      {!beschikbaar && (
        <p className="m-0 flex items-center gap-2 border-b bg-amber-50 px-4 py-2 text-sm text-amber-900">
          <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />Feedback is nog niet beschikbaar (migratie 098 staat nog niet in de database).
        </p>
      )}
      <Verzoeken verzoeken={verzoeken} voorMij={voorMij} doc={doc} bezig={acties.klaar.isPending} onKlaar={(id) => acties.klaar.mutate(id, {
        onSuccess: () => toast({ title: 'Klaar met feedback', description: 'De vrager krijgt een melding.' }),
        onError: (e) => toast({ title: 'Niet gelukt', description: (e as Error).message, variant: 'destructive' }),
      })} />

      <div className="grid min-h-0 flex-1 grid-rows-[minmax(0,1fr)_minmax(0,45%)] lg:grid-cols-[minmax(0,1fr)_400px] lg:grid-rows-1">
        <main className="flex min-h-0 flex-col bg-muted/40">
          {soort === 'pdf' ? (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b bg-background/80 px-4 py-1.5 text-xs text-muted-foreground">
                <Button
                  variant={plaatsen ? 'secondary' : 'ghost'}
                  size="sm"
                  className="h-7 text-xs"
                  aria-pressed={plaatsen}
                  disabled={!beschikbaar}
                  onClick={() => setPlaatsen(!plaatsen)}
                >
                  <MousePointerClick className="mr-1.5 h-3.5 w-3.5" />Opmerking plaatsen
                </Button>
                <span className="hidden sm:inline">{plaatsen ? 'Klik op de pagina voor een punt, sleep voor een kader.' : 'Alleen lezen.'}</span>
                <span className="ml-auto flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Kleiner" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}><ZoomOut className="h-4 w-4" /></Button>
                  <span className="w-10 text-center tabular-nums">{Math.round(zoom * 100)}%</span>
                  <Button variant="ghost" size="icon" className="h-7 w-7" aria-label="Groter" onClick={() => setZoom((z) => Math.min(2.5, +(z + 0.25).toFixed(2)))}><ZoomIn className="h-4 w-4" /></Button>
                </span>
              </div>
              {pdf.isPending ? (
                <div className="flex flex-1 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="PDF laden" /></div>
              ) : pdf.isError ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-sm text-muted-foreground">
                  <p className="max-w-md">De PDF kon hier niet geladen worden: {(pdf.error as Error).message}</p>
                  <p className="max-w-md">De punten staan rechts, met hun pagina; open het bestand in SharePoint om mee te kijken.</p>
                </div>
              ) : (
                <PdfPaginas
                  blob={pdf.data!}
                  zoom={zoom}
                  hier={hier}
                  geesten={geesten}
                  nrs={nrs}
                  concept={concept}
                  plaatsen={plaatsen && beschikbaar}
                  gekozen={gekozen}
                  onKies={setGekozen}
                  onAnker={setConcept}
                  doc={doc}
                />
              )}
            </>
          ) : (
            <GeenVoorbeeld variant={variant.file} soort={soort} />
          )}
        </main>

        <Punten
          doc={doc}
          fileId={fileId}
          soort={soort}
          alle={alle}
          nrs={nrs}
          mijnId={mijnId}
          concept={concept}
          setConcept={setConcept}
          gekozen={gekozen}
          setGekozen={setGekozen}
          beschikbaar={beschikbaar}
          plaatsBezig={acties.plaats.isPending}
          onPlaats={plaats}
          acties={acties}
        />
      </div>
    </>
  );
}

function GeenVoorbeeld({ variant, soort }: { variant: Bestand; soort: Soort }) {
  const wat = soort === 'dia' ? 'een presentatie' : soort === 'blad' ? 'een spreadsheet' : soort === 'pagina' ? 'een Word-document' : 'dit bestand';
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
      <FileText className="h-10 w-10 text-slate-500" aria-hidden />
      <p className="m-0 max-w-md break-all text-sm font-medium">{variant.name}</p>
      <p className="m-0 max-w-md text-sm text-muted-foreground">
        Van {wat} tonen we hier geen pagina's. Zet rechts een opmerking, met het nummer van de {NAAM_SOORT[soort].toLowerCase()} als
        het over één plek gaat.
      </p>
      {variant.web_url && (
        <Button variant="outline" size="sm" asChild>
          <a href={variant.web_url} target="_blank" rel="noreferrer"><ExternalLink className="mr-1.5 h-4 w-4" />Openen in SharePoint</a>
        </Button>
      )}
    </div>
  );
}

function Verzoeken({ verzoeken, voorMij, doc, bezig, onKlaar }: {
  verzoeken: FeedbackVerzoek[];
  voorMij: FeedbackVerzoek[];
  doc: DocumentGroup<Bestand>;
  bezig: boolean;
  onKlaar: (id: string) => void;
}) {
  if (verzoeken.length === 0) return null;
  const versieNr = (v: FeedbackVerzoek) => versieVan(doc, v.file_id)?.version ?? v.version;
  const anderen = verzoeken.filter((v) => !voorMij.includes(v)).slice(0, 2);
  return (
    <div className="flex flex-col border-b">
      {voorMij.map((v) => (
        <div key={v.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 bg-amber-50 px-4 py-2.5 text-sm text-amber-950">
          <BellRing className="h-4 w-4 shrink-0" aria-hidden />
          <span className="min-w-0 flex-1">
            <strong>{v.requested_by_name ?? 'Iemand'}</strong> vraagt je feedback op v{versieNr(v)}
            {v.question ? <>: <q className="italic">{v.question}</q></> : '.'}
            {v.due_on && <span className="whitespace-nowrap text-amber-800"> · vóór {datum(v.due_on)}</span>}
          </span>
          <Button size="sm" variant="outline" className="h-8 border-amber-300 bg-white" disabled={bezig} onClick={() => onKlaar(v.id)}>
            <Check className="mr-1.5 h-4 w-4" />Klaar met feedback
          </Button>
        </div>
      ))}
      {anderen.map((v) => (
        <div key={v.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-1.5 text-xs text-muted-foreground">
          <span>
            {datum(v.created_at)} · {v.requested_by_name ?? 'Iemand'} vroeg feedback op v{versieNr(v)}
            {v.due_on ? ` (vóór ${datum(v.due_on)})` : ''} aan
          </span>
          {v.recipients.map((r) => (
            <span
              key={r.user_id}
              title={pushLabel(r)}
              className={cn('flex items-center gap-1 rounded-full border px-2 py-px', r.done ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : 'bg-background')}
            >
              {r.done && <Check className="h-3 w-3" aria-hidden />}
              {r.name ?? 'iemand'}
              {r.push_status === 'sent' && <Smartphone className="h-3 w-3" aria-label="push verstuurd" />}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

/* ─── PDF met punten ─── */

function PdfPaginas({ blob, zoom, hier, geesten, nrs, concept, plaatsen, gekozen, onKies, onAnker, doc }: {
  blob: Blob;
  zoom: number;
  hier: FeedbackPunt[];
  geesten: FeedbackPunt[];
  nrs: Map<string, number>;
  concept: Anker | null;
  plaatsen: boolean;
  gekozen: string | null;
  onKies: (id: string) => void;
  onAnker: (a: Anker) => void;
  doc: DocumentGroup<Bestand>;
}) {
  const houder = useRef<HTMLDivElement>(null);
  const [basis, setBasis] = useState(800);
  const [aantal, setAantal] = useState(0);
  const [fout, setFout] = useState<string | null>(null);

  useEffect(() => {
    const el = houder.current;
    if (!el) return;
    const meet = () => setBasis(Math.max(280, Math.min(980, el.clientWidth - 48)));
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* Een gekozen punt in beeld schuiven. */
  useEffect(() => {
    if (!gekozen) return;
    houder.current?.querySelector(`[data-punt="${gekozen}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
  }, [gekozen]);

  const breedte = Math.round(basis * zoom);
  const versieNr = (p: FeedbackPunt) => versieVan(doc, p.file_id)?.version ?? p.version;

  return (
    <div ref={houder} className="min-h-0 flex-1 overflow-auto px-6 py-6">
      {fout ? (
        <p className="text-center text-sm text-muted-foreground">De PDF kon niet gelezen worden: {fout}</p>
      ) : (
        <Document
          file={blob}
          onLoadSuccess={({ numPages }) => setAantal(numPages)}
          onLoadError={(e) => setFout(e.message)}
          loading={<div className="mx-auto h-[70vh] animate-pulse rounded bg-muted" style={{ width: breedte }} />}
        >
          {Array.from({ length: aantal }, (_, i) => i + 1).map((n) => (
            <PaginaMetPunten
              key={n}
              nummer={n}
              breedte={breedte}
              punten={hier.filter((p) => p.page === n && p.x !== null)}
              geesten={geesten.filter((p) => p.page === n && p.x !== null)}
              nrs={nrs}
              concept={concept?.page === n && concept.x !== undefined && concept.x !== null ? concept : null}
              plaatsen={plaatsen}
              gekozen={gekozen}
              onKies={onKies}
              onAnker={onAnker}
              versieNr={versieNr}
            />
          ))}
        </Document>
      )}
    </div>
  );
}

function PaginaMetPunten({ nummer, breedte, punten, geesten, nrs, concept, plaatsen, gekozen, onKies, onAnker, versieNr }: {
  nummer: number;
  breedte: number;
  punten: FeedbackPunt[];
  geesten: FeedbackPunt[];
  nrs: Map<string, number>;
  concept: Anker | null;
  plaatsen: boolean;
  gekozen: string | null;
  onKies: (id: string) => void;
  onAnker: (a: Anker) => void;
  versieNr: (p: FeedbackPunt) => string | null;
}) {
  const laag = useRef<HTMLDivElement>(null);
  const [sleep, setSleep] = useState<{ start: { x: number; y: number }; eind: { x: number; y: number } } | null>(null);
  const [hoogte, setHoogte] = useState(Math.round(breedte * 1.414));

  const fractie = (e: React.PointerEvent) => {
    const r = laag.current!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height, r };
  };

  return (
    <div className="relative mx-auto mb-5 bg-white shadow-sm ring-1 ring-black/5" style={{ width: breedte }} data-pagina={nummer}>
      <Page
        pageNumber={nummer}
        width={breedte}
        renderTextLayer={false}
        renderAnnotationLayer={false}
        onRenderSuccess={() => setHoogte(laag.current?.getBoundingClientRect().height ?? hoogte)}
        loading={<div style={{ height: hoogte }} className="animate-pulse bg-muted" />}
      />
      <div
        ref={laag}
        className={cn('absolute inset-0 touch-none', plaatsen ? 'cursor-crosshair' : 'pointer-events-none')}
        aria-label={`Pagina ${nummer}`}
        onPointerDown={(e) => {
          if (!plaatsen || e.button !== 0) return;
          const { x, y } = fractie(e);
          e.currentTarget.setPointerCapture(e.pointerId);
          setSleep({ start: { x, y }, eind: { x, y } });
        }}
        onPointerMove={(e) => {
          if (!sleep) return;
          const { x, y } = fractie(e);
          setSleep({ ...sleep, eind: { x, y } });
        }}
        onPointerUp={(e) => {
          if (!sleep) return;
          const { x, y, r } = fractie(e);
          setSleep(null);
          onAnker(ankerUitSleep(nummer, sleep.start, { x, y }, { breedte: r.width, hoogte: r.height }));
        }}
        onPointerCancel={() => setSleep(null)}
      />
      {sleep && (Math.abs(sleep.eind.x - sleep.start.x) > 0.01 || Math.abs(sleep.eind.y - sleep.start.y) > 0.01) && (
        <div
          className="pointer-events-none absolute border-2 border-dashed border-blue-500 bg-blue-400/10"
          style={{
            left: `${Math.min(sleep.start.x, sleep.eind.x) * 100}%`,
            top: `${Math.min(sleep.start.y, sleep.eind.y) * 100}%`,
            width: `${Math.abs(sleep.eind.x - sleep.start.x) * 100}%`,
            height: `${Math.abs(sleep.eind.y - sleep.start.y) * 100}%`,
          }}
        />
      )}
      {geesten.map((p) => (
        <Merk key={p.id} punt={p} nr={nrs.get(p.id)!} geest gekozen={gekozen === p.id} onKies={onKies} titel={`Open punt van v${versieNr(p)}: ${p.body}`} />
      ))}
      {punten.map((p) => (
        <Merk key={p.id} punt={p} nr={nrs.get(p.id)!} gekozen={gekozen === p.id} onKies={onKies} titel={p.body} />
      ))}
      {concept && (
        <Merk
          punt={{ id: 'concept', status: 'open', x: concept.x!, y: concept.y!, w: concept.w ?? null, h: concept.h ?? null } as FeedbackPunt}
          nr={0}
          concept
          gekozen
          onKies={() => undefined}
          titel="Nieuw punt"
        />
      )}
      <span className="pointer-events-none absolute -left-8 top-1 hidden text-xs tabular-nums text-muted-foreground sm:block">{nummer}</span>
    </div>
  );
}

function Merk({ punt, nr, geest = false, concept = false, gekozen, onKies, titel }: {
  punt: Pick<FeedbackPunt, 'id' | 'status' | 'x' | 'y' | 'w' | 'h'>;
  nr: number;
  geest?: boolean;
  concept?: boolean;
  gekozen: boolean;
  onKies: (id: string) => void;
  titel: string;
}) {
  const kader = punt.w !== null && punt.h !== null && punt.w !== undefined;
  const bol = cn(
    'flex h-6 min-w-6 items-center justify-center rounded-full border-2 px-1 text-[11px] font-bold shadow-sm',
    concept ? 'border-blue-600 bg-blue-600 text-white' : geest ? 'border-dashed border-amber-500 bg-white/90 text-amber-800' : KLEUR[punt.status],
    gekozen && 'ring-2 ring-foreground ring-offset-1',
  );
  const knop = (
    <button
      type="button"
      data-punt={punt.id}
      title={titel}
      aria-label={concept ? 'Nieuw punt' : `Punt ${nr}${geest ? ' van een eerdere versie' : ''}`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.stopPropagation();
        onKies(punt.id);
      }}
      className={bol}
    >
      {concept ? '+' : nr}
    </button>
  );
  if (kader) {
    return (
      <div
        className={cn('pointer-events-none absolute rounded-sm border-2', concept ? 'border-blue-600 bg-blue-500/10' : geest ? 'border-dashed border-amber-500 bg-amber-300/5' : KADER[punt.status])}
        style={{ left: `${punt.x! * 100}%`, top: `${punt.y! * 100}%`, width: `${punt.w! * 100}%`, height: `${punt.h! * 100}%` }}
      >
        <span className="pointer-events-auto absolute -left-3 -top-3">{knop}</span>
      </div>
    );
  }
  return (
    <span className="absolute -translate-x-1/2 -translate-y-1/2" style={{ left: `${punt.x! * 100}%`, top: `${punt.y! * 100}%` }}>
      {knop}
    </span>
  );
}

/* ─── De punten rechts ─── */

type Acties = ReturnType<typeof useFeedbackActies>;

function Punten({ doc, fileId, soort, alle, nrs, mijnId, concept, setConcept, gekozen, setGekozen, beschikbaar, plaatsBezig, onPlaats, acties }: {
  doc: DocumentGroup<Bestand>;
  fileId: string;
  soort: Soort;
  alle: FeedbackPunt[];
  nrs: Map<string, number>;
  mijnId: string | null;
  concept: Anker | null;
  setConcept: (a: Anker | null) => void;
  gekozen: string | null;
  setGekozen: (id: string | null) => void;
  beschikbaar: boolean;
  plaatsBezig: boolean;
  onPlaats: (tekst: string, anker: Anker) => void;
  acties: Acties;
}) {
  const [tab, setTab] = useState<Tab>('open');
  const lijst = useRef<HTMLDivElement>(null);
  const t = telling(alle);
  const zichtbaar = [...alle]
    .filter((p) => (tab === 'alles' ? true : tab === 'open' ? p.status === 'open' : p.status !== 'open'))
    .sort((a, b) => nrs.get(a.id)! - nrs.get(b.id)!);

  useEffect(() => {
    if (!gekozen) return;
    lijst.current?.querySelector(`[data-kaart="${gekozen}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [gekozen]);

  useEffect(() => {
    if (concept) lijst.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [concept]);

  const tabs: [Tab, string, number][] = [['open', 'Open', t.open], ['dicht', 'Afgehandeld', t.totaal - t.open], ['alles', 'Alles', t.totaal]];

  return (
    <aside className="flex min-h-0 flex-col border-t bg-background lg:border-l lg:border-t-0" aria-label="Feedbackpunten">
      <div className="flex flex-wrap items-center gap-1.5 border-b px-4 py-2.5">
        <div role="tablist" aria-label="Punten" className="flex gap-1 text-[13px]">
          {tabs.map(([id, label, n]) => (
            <button
              key={id}
              role="tab"
              type="button"
              aria-selected={tab === id}
              onClick={() => setTab(id)}
              className={cn('h-7 rounded-full border px-2.5', tab === id ? 'border-foreground bg-foreground font-medium text-background' : 'bg-background text-muted-foreground hover:text-foreground')}
            >
              {label} <span className="opacity-70">{n}</span>
            </button>
          ))}
        </div>
        {beschikbaar && !concept && (
          <Button variant="ghost" size="sm" className="ml-auto h-7 px-2 text-xs" onClick={() => setConcept({ page: null })}>
            <MessageSquarePlus className="mr-1 h-3.5 w-3.5" />{soort === 'pdf' ? 'Hele document' : 'Opmerking'}
          </Button>
        )}
      </div>
      <div ref={lijst} className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        {concept && (
          <NieuwPunt
            key={JSON.stringify(concept)}
            soort={soort}
            anker={concept}
            bezig={plaatsBezig}
            onAnnuleer={() => setConcept(null)}
            onOpslaan={onPlaats}
          />
        )}
        {zichtbaar.length === 0 && !concept && (
          <p className="m-0 py-6 text-center text-sm text-muted-foreground">
            {alle.length === 0
              ? soort === 'pdf' ? 'Nog geen feedback. Klik op de pagina om een punt te plaatsen.' : 'Nog geen feedback. Kies "Opmerking" om er een te plaatsen.'
              : tab === 'open' ? 'Geen open punten.' : 'Nog niets afgehandeld.'}
          </p>
        )}
        {zichtbaar.map((p) => (
          <PuntKaart
            key={p.id}
            punt={p}
            nr={nrs.get(p.id)!}
            doc={doc}
            fileId={fileId}
            soort={soort}
            mijnId={mijnId}
            gekozen={gekozen === p.id}
            onKies={() => setGekozen(p.id)}
            acties={acties}
          />
        ))}
      </div>
    </aside>
  );
}

function NieuwPunt({ soort, anker, bezig, onAnnuleer, onOpslaan }: {
  soort: Soort;
  anker: Anker;
  bezig: boolean;
  onAnnuleer: () => void;
  onOpslaan: (tekst: string, anker: Anker) => void;
}) {
  const [tekst, setTekst] = useState('');
  const [pagina, setPagina] = useState('');
  const vrijePagina = soort !== 'pdf';
  const plek: Anker = vrijePagina ? { page: /^\d+$/.test(pagina) && Number(pagina) >= 1 ? Number(pagina) : null } : anker;
  const kan = tekst.trim().length > 0 && !bezig;
  const wat = anker.w ? 'Kader' : anker.x !== undefined && anker.x !== null ? 'Punt' : 'Opmerking';
  const opslaan = () => kan && onOpslaan(tekst, plek);

  return (
    <div className="flex flex-col gap-2 rounded-[10px] border border-blue-200 bg-blue-50/70 p-3">
      <div className="flex items-center gap-2 text-sm">
        <strong>{wat}</strong>
        {!vrijePagina && <span className="text-muted-foreground">{plekLabel(anker, soort)}</span>}
        {vrijePagina && (
          <label className="flex items-center gap-1.5 text-[13px] text-muted-foreground">
            {NAAM_SOORT[soort]}
            <Input
              value={pagina}
              onChange={(e) => setPagina(e.target.value.replace(/\D/g, '').slice(0, 5))}
              inputMode="numeric"
              placeholder="—"
              aria-label={`${NAAM_SOORT[soort]}nummer (optioneel)`}
              className="h-7 w-16 bg-background"
            />
          </label>
        )}
      </div>
      <Textarea
        autoFocus
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            opslaan();
          }
        }}
        placeholder="Wat moet er anders?"
        rows={3}
        maxLength={4000}
        className="bg-background"
      />
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={!kan} onClick={opslaan}>
          {bezig && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Plaatsen
        </Button>
        <Button size="sm" variant="ghost" onClick={onAnnuleer}>Annuleren</Button>
        <span className="ml-auto hidden text-[11px] text-muted-foreground sm:inline">⌘↵ plaatst</span>
      </div>
    </div>
  );
}

function PuntKaart({ punt, nr, doc, fileId, soort, mijnId, gekozen, onKies, acties }: {
  punt: FeedbackPunt;
  nr: number;
  doc: DocumentGroup<Bestand>;
  fileId: string;
  soort: Soort;
  mijnId: string | null;
  gekozen: boolean;
  onKies: () => void;
  acties: Acties;
}) {
  const { toast } = useToast();
  const [antwoord, setAntwoord] = useState('');
  const [reden, setReden] = useState<string | null>(null);
  const eigenVersie = versieVan(doc, punt.file_id);
  const nieuwste = doc.versions[0]!;
  const andereVersie = punt.file_id !== fileId;
  /* "Verwerkt in" wijst naar de nieuwste versie als die na de versie van het punt komt. */
  const doel = eigenVersie && compareRank(nieuwste.rank, eigenVersie.rank) > 0 ? nieuwste : null;
  const mislukt = (e: unknown) => toast({ title: 'Niet gelukt', description: (e as Error).message, variant: 'destructive' });

  const stuurAntwoord = () => {
    if (!antwoord.trim()) return;
    acties.reageer.mutate({ puntId: punt.id, tekst: antwoord }, { onSuccess: () => setAntwoord(''), onError: mislukt });
  };

  return (
    <article
      data-kaart={punt.id}
      onClick={onKies}
      className={cn('flex cursor-default flex-col gap-2 rounded-[10px] border bg-background p-3 text-sm', gekozen && 'border-foreground ring-1 ring-foreground')}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className={cn('flex h-6 min-w-6 items-center justify-center rounded-full border-2 px-1 text-[11px] font-bold', KLEUR[punt.status])}>{nr}</span>
        <strong>{punt.author_name ?? 'Onbekend'}</strong>
        <span className="text-xs text-muted-foreground">{datum(punt.created_at)} {tijd(punt.created_at)}</span>
        <span className="text-xs text-muted-foreground">· {plekLabel(punt, soort)}</span>
        {andereVersie && <span className="rounded border px-1.5 text-[11px] text-muted-foreground">op v{eigenVersie?.version ?? punt.version ?? '?'}</span>}
        <span
          className={cn(
            'ml-auto rounded-full px-2 py-px text-[11px] font-medium',
            punt.status === 'open' ? 'bg-amber-100 text-amber-900' : punt.status === 'resolved' ? 'bg-emerald-100 text-emerald-900' : 'bg-slate-100 text-slate-700',
          )}
        >
          {statusLabel(punt)}
        </span>
      </header>
      <p className="m-0 whitespace-pre-wrap break-words leading-relaxed">{punt.body}</p>

      {punt.replies.length > 0 && (
        <ol className="m-0 flex list-none flex-col gap-1.5 border-l-2 pl-3">
          {punt.replies.map((r) => (
            <li key={r.id} className={cn('text-[13px]', r.kind === 'status' && 'text-muted-foreground')}>
              <span className="font-medium">{r.author_name ?? 'Onbekend'}</span>{' '}
              <span className="text-xs text-muted-foreground">{datum(r.created_at)} {tijd(r.created_at)}</span>
              <p className={cn('m-0 whitespace-pre-wrap break-words', r.kind === 'status' && 'italic')}>{r.body}</p>
            </li>
          ))}
        </ol>
      )}

      <div className="flex items-end gap-2" onClick={(e) => e.stopPropagation()}>
        <CornerDownRight className="mb-2 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <Textarea
          value={antwoord}
          onChange={(e) => setAntwoord(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              stuurAntwoord();
            }
          }}
          placeholder="Reageer…"
          rows={1}
          maxLength={4000}
          className="min-h-8 resize-none py-1.5 text-[13px]"
          aria-label={`Reageer op punt ${nr}`}
        />
        {antwoord.trim() && (
          <Button size="sm" className="h-8" disabled={acties.reageer.isPending} onClick={stuurAntwoord}>Stuur</Button>
        )}
      </div>

      {reden !== null && (
        <div className="flex flex-col gap-2 rounded-md border bg-muted/40 p-2" onClick={(e) => e.stopPropagation()}>
          <Input autoFocus value={reden} onChange={(e) => setReden(e.target.value)} placeholder="Waarom niet? (verplicht)" className="h-8 bg-background text-[13px]" />
          <div className="flex gap-2">
            <Button
              size="sm"
              className="h-7 text-xs"
              disabled={reden.trim().length < 3 || acties.status.isPending}
              onClick={() => acties.status.mutate({ puntId: punt.id, status: 'dismissed', notitie: reden }, { onSuccess: () => setReden(null), onError: mislukt })}
            >
              Niet overnemen
            </Button>
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReden(null)}>Annuleren</Button>
          </div>
        </div>
      )}

      <footer className="flex flex-wrap items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
        {punt.status === 'open' ? (
          <>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              disabled={acties.status.isPending}
              onClick={() => acties.status.mutate(
                { puntId: punt.id, status: 'resolved', versie: doel ? refVan(doel.files[0]!, doel.version) : null },
                { onError: mislukt },
              )}
            >
              <Check className="mr-1 h-3.5 w-3.5" />{doel ? `Verwerkt in v${doel.version}` : 'Verwerkt'}
            </Button>
            {reden === null && (
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setReden('')}>
                <Ban className="mr-1 h-3.5 w-3.5" />Niet overnemen
              </Button>
            )}
          </>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            disabled={acties.status.isPending}
            onClick={() => acties.status.mutate({ puntId: punt.id, status: 'open' }, { onError: mislukt })}
          >
            <Undo2 className="mr-1 h-3.5 w-3.5" />Heropenen
          </Button>
        )}
        {punt.author_id === mijnId && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 px-2 text-xs text-muted-foreground hover:text-red-700"
            disabled={acties.weg.isPending}
            aria-label={`Punt ${nr} weghalen`}
            onClick={() => {
              if (window.confirm('Dit punt weghalen? Reacties verdwijnen mee uit beeld.')) acties.weg.mutate(punt.id, { onError: mislukt });
            }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </footer>
    </article>
  );
}
